import { Controller } from "@hotwired/stimulus";
import { getJson } from "~/lib/http";

export default class extends Controller<HTMLInputElement> {
  static values = {
    url: String,
    autoPopulated: Boolean,
  };

  declare readonly urlValue: string;
  declare autoPopulatedValue: boolean;
  private errorElement: HTMLElement | null = null;
  private abortController: AbortController | null = null;
  private lastValidatedValue = "";

  validate() {
    // autoPopulatedValue tracks whether the current name was set by repo auto-fill.
    // If true and duplicate: auto-rename. If false and duplicate: show error.
    // Flag is cleared when user manually edits the field (value changes).
    const name = this.element.value.trim();

    if (!name) {
      this.clearError();
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const wasAutoPopulated = this.autoPopulatedValue;

    if (name !== this.lastValidatedValue) {
      this.autoPopulatedValue = false;
    }
    this.lastValidatedValue = name;

    getJson<{ exists: boolean; suggested_name?: string }>(
      `${this.urlValue}?name=${encodeURIComponent(name)}`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((data) => {
        if (data.exists) {
          if (wasAutoPopulated && data.suggested_name) {
            this.element.value = data.suggested_name;
            this.lastValidatedValue = data.suggested_name;
            this.autoPopulatedValue = false;
            this.clearError();
          } else {
            this.showError("This name is already being used by another pipeline");
          }
        } else {
          this.clearError();
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Name validation error:", error);
        }
      });
  }

  showError(message: string) {
    this.clearError();

    this.errorElement = document.createElement("p");
    this.errorElement.className = "FormField__Error";
    this.errorElement.textContent = message;
    this.errorElement.id = `${this.element.id}-validation-error`;

    this.element.insertAdjacentElement("afterend", this.errorElement);
    this.element.classList.add("is-error");

    const formField = this.element.closest(".FormField");
    if (formField) {
      formField.classList.add("FormField--Errored");
    }
  }

  clearError() {
    if (this.errorElement) {
      this.errorElement.remove();
      this.errorElement = null;
    }

    this.element.classList.remove("is-error");

    const formField = this.element.closest(".FormField");
    if (formField) {
      const railsErrors = formField.querySelectorAll(
        `.FormField__Error:not(#${this.element.id}-validation-error)`,
      );
      railsErrors.forEach((error) => error.remove());
      formField.classList.remove("FormField--Errored");
    }
  }

  submitForm() {
    this.clearError();
  }
}
